import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, AutocompleteChangeReason, AutocompleteHighlightChangeReason, Checkbox, Chip, createFilterOptions, Popper, PopperProps, TextField, Typography } from '@mui/material';
import { SelectProps } from '@mui/material/Select/Select';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { ChangeEvent, memo, ReactNode, useCallback, useMemo, useState } from 'react';

import { IOption } from '@shared/interfaces';
import { AutoCompleteWrapper, CustomMessageWrapper, GroupHeader, InputLabel } from '@styles/InputStyles';
import { GroupItems } from '@shared/styledComponents';

const filter = createFilterOptions<IOption>();

export interface ISelectProps extends Omit<SelectProps, 'onChange'> {
  label?: string;
  backgroundColor?: string;
  value: unknown | unknown[];
  options: IOption[];
  onChange(value: unknown | unknown[], option?: IOption): void;
  isLoading?: boolean;
  helperText?: ReactNode;
  error?: boolean;
  onTextInputChange?: (val: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  displayError?: boolean;
  fullWidth?: boolean;
  dense?: boolean;
  disabled?: boolean;
  hideNoneOption?: boolean;
  placeholder?: string;
  isCreatable?: boolean;
  multiple?: boolean;
  freeSolo?: boolean;
  canBeClean?: boolean;
  renderOption?: boolean;
  openOnFocus?: boolean;
  customMessage?: {
    message: string, 
    color: string, 
    textTransform?: string,
    textAlign?: string
  };
  highlightFullBorder?: boolean;
  isFitContentWidth?: boolean;
  removeExtraPadding?: boolean;
  limitTags?: number;
  placeholderOpacity?: number;
  defaultValue?: IOption<unknown>;
  getOptionDisabled?: (option: IOption) => boolean;
  checkboxes?: boolean
}

const PopperAuto = (props: PopperProps) => <Popper {...props} style={{maxWidth: 'fit-content'}} placement="bottom-start" />;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SelectAutocompleteBase = ({
  label,
  value,
  defaultValue,
  placeholder,
  backgroundColor,
  options = [],
  onChange,
  onTextInputChange,
  isLoading,
  helperText,
  displayError,
  fullWidth,
  required,
  error,
  disabled,
  margin,
  isFitContentWidth,
  limitTags,
  getOptionDisabled,
  placeholderOpacity,
  openOnFocus = true,
  canBeClean = true,
  freeSolo    = false,
  multiple    = false,
  isCreatable = false,
  customMessage = {
    message: '',
    color: '',
    textTransform: '',
    textAlign: ''
  },
  highlightFullBorder = false,
  removeExtraPadding = false,
  size,
  checkboxes
}: ISelectProps) => {
  const isGroupedBy = options.some(option => option.groupLabel);
  const errorRequired = displayError && required && !!value;
  const errorRequiredText = errorRequired && `${label} is required`;
  const [highlightResason, setHighlightResason] = useState('');
  const [searchedValue, setSearchedValue] = useState('');

  const shouldShrinkLabel = multiple && Array.isArray(value) && value.length > 0 && size === 'small';

  const handleOnChange = useCallback(
    (_event: React.SyntheticEvent, selectedValue: string | IOption<unknown> | (string | IOption<unknown>)[] | null, reason: AutocompleteChangeReason) => {
      // TO make autSelect working on Tab of a dropdpnw we need to ake that on mouse hover of an optio and user don;t select that and click  outsoide that option doesn;t set as value
      // by default that happens with autoSelect. Following isNotTab Scenario will prevent it.
      const isNotTabScenario = reason === 'blur' && (highlightResason === 'mouse' || highlightResason === 'auto') && !searchedValue;
      setSearchedValue('');
      if ((reason === 'clear' && !canBeClean) || isNotTabScenario) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      if (reason === 'removeOption' && _event.nativeEvent?.type === 'focusout' && !selectedValue.includes(_event.target?.innerText)) {
        return;
      }

      if (Array.isArray(selectedValue)) {
        onChange(selectedValue.map((option) => (option as IOption).value));
      } else {
        onChange((selectedValue as IOption)?.value ?? '', selectedValue as IOption);
      }
    },
    [value, canBeClean, onChange, highlightResason, searchedValue],
  );

  const selectedOption = useMemo(() => {

    if (Array.isArray(value)) {
      const seletedValuesArray = options.filter((option) => value.includes(option.value));
      return seletedValuesArray;
    }

    if (value !== null && typeof value === 'object' && 'value' in value) {
      const chosenOption = options.find((option) => (option.value as IOption<unknown>)?.value == value.value);

      return chosenOption;
    }

    if (value || value == 0) {
      const selectedValue = options.find((option) => option.value == value) ?? { value: value as string, label: isCreatable ? value as string : '' };
      return selectedValue;
    }

    return multiple ? [] : null;
  }, [value, options, multiple, isCreatable]);

  const onHighlightChange = useCallback((event: React.SyntheticEvent, option: IOption<unknown> | null, reason: AutocompleteHighlightChangeReason) => {
    setHighlightResason(reason);
  }, []);

  const onTextFieldChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchedValue(event.target.value ?? '');
    onTextInputChange?.(event);
  }, [onTextInputChange]);

  return (
    <AutoCompleteWrapper
      labeled={Boolean(label)}
      multiple={multiple}
      margin={margin}
      backgroundColor={backgroundColor}
      removeExtraPadding={removeExtraPadding}
      placeholderOpacity={placeholderOpacity}
      highlightFullBorder={highlightFullBorder}
    >
      {label && <InputLabel isShrunk={shouldShrinkLabel}>{label}</InputLabel>}

      <Autocomplete
        autoHighlight
        autoSelect
        autoFocus
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        clearIcon={canBeClean ? <ClearIcon fontSize="small" /> : false}
        PopperComponent={isFitContentWidth ? PopperAuto : undefined}
        openOnFocus={openOnFocus}
        freeSolo={freeSolo}
        multiple={multiple}
        value={selectedOption}
        loading={isLoading}
        disabled={disabled}
        fullWidth={fullWidth}
        options={options}
        defaultValue={defaultValue as IOption}
        limitTags={limitTags}
        size={size ?? size}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        groupBy={isGroupedBy ? (option) => (option as IOption).groupLabel ?? '' : undefined}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={(option) => (option as IOption).label ?? ''}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={((option.value as IOption<unknown>)?.value || option.value) as string}>
            {checkboxes && <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />}
            {option.label}
          </li>
        )}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (isCreatable) {
            const { inputValue } = params;
            // Suggest the creation of a new value
            const isExisting = options.some((option) => inputValue === option.label);
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                value: inputValue,
                label: `Add "${inputValue}"`,
              });
            }
          }

          return filtered;
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            error={!!error}
            placeholder={placeholder}
            helperText={helperText || errorRequiredText}
            onChange={onTextFieldChange}
          />
        )}
        {...(limitTags && {
          renderTags: (value, getTagProps) => {
            const numTags = value.length;
            const limit = numTags > limitTags && ` +${numTags - limitTags}`;
            return (
              <>
                {value.slice(0, limitTags).map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option.label}
                    size='small'
                  />
                ))}
                <Typography margin="-5px 0 0 3px">
                  {limit}
                </Typography>
              </>
            );
          }
        })}
        onChange={handleOnChange}
        onHighlightChange={onHighlightChange}
      />
      {customMessage && 
      <CustomMessageWrapper 
        color={customMessage.color} 
        textTransform={customMessage.textTransform}
        textAlign={customMessage.textAlign}
      >{customMessage.message}</CustomMessageWrapper>}
    </AutoCompleteWrapper>
  );
};

export const SelectAutocomplete = memo(SelectAutocompleteBase);
